import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Sparkle, Image as ImageIcon, ArrowClockwise, Trash, Copy, DownloadSimple,
  Robot, TruckTrailer, Palette, Cube, Money, Clock, Heart, Lightning,
  CaretRight, Confetti, X as XIcon, Stack, CheckCircle, WarningCircle,
} from "@phosphor-icons/react";
import client from "../lib/api";

const AUDIENCES = [
  { id: "owner_op", label: "Owner-operator", subtitle: "1 truck · drives themselves", icon: TruckTrailer },
  { id: "small_fleet", label: "Small fleet", subtitle: "2-25 trucks · owner runs ops", icon: TruckTrailer },
  { id: "mid_fleet", label: "Mid-fleet", subtitle: "25-100 trucks · dedicated dispatch", icon: TruckTrailer },
];

const TONES = [
  { id: "money", label: "Money-focused", subtitle: "Lead with dollars saved", icon: Money },
  { id: "time", label: "Time-focused", subtitle: "Lead with hours back", icon: Clock },
  { id: "peace_of_mind", label: "Peace of mind", subtitle: "Stop worrying about X", icon: Heart },
  { id: "bold", label: "Bold contrarian", subtitle: "Kill the spreadsheet", icon: Lightning },
];

const IMAGE_STYLES = [
  { id: "dashboard_mockup", label: "Dashboard mockup", subtitle: "Laptop showing FreightPilot", icon: Robot },
  { id: "trucker_hero", label: "Trucker hero", subtitle: "Photo of driver + rig", icon: TruckTrailer },
  { id: "illustration", label: "Illustration", subtitle: "Editorial vector art", icon: Palette },
  { id: "abstract", label: "Abstract 3D", subtitle: "No people · pure concept", icon: Cube },
];

const AUDIENCE_LABEL = Object.fromEntries(AUDIENCES.map((a) => [a.id, a.label]));
const TONE_LABEL = Object.fromEntries(TONES.map((t) => [t.id, t.label]));

function pill(label, active, onClick, Icon, subtitle, testid) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-left p-3 rounded-lg border transition-all ${
        active
          ? "border-[#FF5A1F] bg-[#FF5A1F]/10 ring-2 ring-[#FF5A1F]/20"
          : "border-white/10 bg-white/[0.02] hover:border-white/25"
      }`}
    >
      <div className="flex items-center gap-2 mb-1">
        <Icon size={14} weight={active ? "fill" : "regular"} className={active ? "text-[#FF5A1F]" : "text-neutral-500"} />
        <div className={`text-sm font-medium ${active ? "text-white" : "text-neutral-300"}`}>{label}</div>
      </div>
      <div className="text-[11px] text-neutral-500 leading-tight">{subtitle}</div>
    </button>
  );
}

function CopyBlock({ label, values, testidPrefix }) {
  const copyOne = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success("Copied"));
  };
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">{label}</div>
      <div className="space-y-2">
        {values.map((v, i) => (
          <div
            key={i}
            data-testid={`${testidPrefix}-${i}`}
            className="group flex items-start gap-2 p-3 rounded-lg border border-white/10 bg-white/[0.02] hover:border-white/25 transition-colors"
          >
            <div className="text-[10px] text-neutral-600 font-mono pt-0.5 min-w-[16px]">#{i + 1}</div>
            <div className="flex-1 text-sm text-white leading-relaxed whitespace-pre-wrap break-words">{v}</div>
            <button
              onClick={() => copyOne(v)}
              className="opacity-40 group-hover:opacity-100 transition-opacity text-neutral-500 hover:text-white"
              title="Copy"
            >
              <Copy size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function CreativeCard({ creative, onOpen, onDelete }) {
  return (
    <div
      className="card-surface overflow-hidden hover:border-white/25 transition-colors cursor-pointer group"
      onClick={onOpen}
      data-testid={`ad-creative-card-${creative.id}`}
    >
      <div className="aspect-square bg-[#141414] flex items-center justify-center relative">
        {creative.has_image ? (
          <AuthedImage cid={creative.id} className="w-full h-full object-cover" />
        ) : (
          <ImageIcon size={40} className="text-neutral-700" />
        )}
        <div className="absolute top-2 right-2 flex items-center gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="rounded-full p-1.5 bg-black/60 backdrop-blur border border-white/10 text-neutral-400 hover:text-rose-300 hover:border-rose-500/40 opacity-0 group-hover:opacity-100 transition-opacity"
            data-testid={`ad-creative-delete-${creative.id}`}
            title="Delete"
          >
            <Trash size={11} />
          </button>
        </div>
      </div>
      <div className="p-3">
        <div className="text-xs font-medium text-white truncate">{creative.product_focus}</div>
        <div className="flex items-center gap-1.5 mt-1">
          <span className="text-[10px] px-1.5 py-0.5 rounded border border-white/10 text-neutral-500">
            {AUDIENCE_LABEL[creative.audience] || creative.audience}
          </span>
          <span className="text-[10px] px-1.5 py-0.5 rounded border border-[#FF5A1F]/30 text-[#FF9764]">
            {TONE_LABEL[creative.tone] || creative.tone}
          </span>
        </div>
      </div>
    </div>
  );
}

function AuthedImage({ cid, className, alt = "Ad creative" }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let alive = true;
    let objectUrl = null;
    (async () => {
      try {
        const res = await client.get(`/admin/ad-studio/${cid}/image`, { responseType: "blob" });
        if (!alive) return;
        objectUrl = URL.createObjectURL(res.data);
        setSrc(objectUrl);
      } catch {/* silent */}
    })();
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [cid]);
  if (!src) {
    return (
      <div className={`${className} flex items-center justify-center bg-[#141414]`}>
        <div className="w-6 h-6 rounded-full border-2 border-neutral-800 border-t-[#FF5A1F] animate-spin" />
      </div>
    );
  }
  return <img src={src} alt={alt} className={className} />;
}

function CreativeDetail({ creative, onClose, onDelete }) {
  if (!creative) return null;
  const downloadImage = async () => {
    try {
      const res = await client.get(`/admin/ad-studio/${creative.id}/image`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const ext = (creative.image_mime && creative.image_mime.includes("jpeg")) ? "jpg" : "png";
      const a = document.createElement("a");
      a.href = url;
      a.download = `freightpilot-ad-${creative.id.slice(0, 8)}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 500);
    } catch {
      toast.error("Download failed");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md p-4 overflow-y-auto" data-testid="ad-detail-modal">
      <div className="min-h-full flex items-start justify-center py-8">
        <div className="w-full max-w-4xl bg-[#0F0F0F] border border-white/10 rounded-xl overflow-hidden shadow-2xl">
          <div className="p-5 border-b border-white/10 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#FF5A1F] font-semibold mb-1">
                <Sparkle size={12} weight="fill" /> Ad creative
              </div>
              <div className="text-lg font-semibold text-white">{creative.product_focus}</div>
              <div className="text-xs text-neutral-500 mt-1">
                {AUDIENCE_LABEL[creative.audience]} · {TONE_LABEL[creative.tone]} · created {new Date(creative.created_at).toLocaleString()}
              </div>
            </div>
            <button onClick={onClose} className="text-neutral-500 hover:text-white" data-testid="ad-detail-close">
              <XIcon size={18} />
            </button>
          </div>
          <div className="p-5 grid lg:grid-cols-2 gap-5">
            <div>
              {creative.has_image ? (
                <div className="rounded-lg overflow-hidden border border-white/10 bg-[#141414]">
                  <AuthedImage cid={creative.id} className="w-full aspect-square object-cover" />
                </div>
              ) : (
                <div className="rounded-lg border border-white/10 bg-[#141414] aspect-square flex items-center justify-center text-neutral-600">
                  <div className="text-center">
                    <ImageIcon size={40} className="mx-auto mb-2" />
                    <div className="text-xs">No image was generated</div>
                  </div>
                </div>
              )}
              {creative.has_image && (
                <button
                  onClick={downloadImage}
                  data-testid="ad-detail-download-btn"
                  className="mt-3 w-full rounded-full px-4 py-2 text-sm border border-white/15 hover:border-white/40 text-neutral-300 hover:text-white transition-colors inline-flex items-center justify-center gap-2"
                >
                  <DownloadSimple size={13} /> Download PNG
                </button>
              )}
            </div>
            <div className="space-y-5">
              <CopyBlock label="Headlines · 40 chars max" values={creative.copy?.headlines || []} testidPrefix="detail-headline" />
              <CopyBlock label="Primary texts · main body" values={creative.copy?.primary_texts || []} testidPrefix="detail-primary" />
              <CopyBlock label="Descriptions · 27 chars max" values={creative.copy?.descriptions || []} testidPrefix="detail-desc" />
            </div>
          </div>
          <div className="p-4 border-t border-white/10 bg-[#0A0A0A] flex items-center justify-end">
            <button
              onClick={onDelete}
              className="text-xs rounded-full px-3 py-1.5 border border-white/10 text-neutral-500 hover:text-rose-300 hover:border-rose-500/40 transition-colors inline-flex items-center gap-1.5"
              data-testid="ad-detail-delete-btn"
            >
              <Trash size={11} /> Delete this creative
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function BatchModal({ open, baseForm, onClose, onDone }) {
  const [count, setCount] = useState(5);
  const [varyTone, setVaryTone] = useState(true);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  if (!open) return null;

  const start = async () => {
    if (!baseForm.product_focus.trim()) {
      toast.error("Fill product focus in the main form first");
      return;
    }
    setRunning(true);
    setResult(null);
    try {
      const { data } = await client.post("/admin/ad-studio/batch", {
        count,
        vary_tone: varyTone,
        base: baseForm,
      }, { timeout: 240000 });
      setResult(data);
      if (data.created > 0) {
        toast.success(`Batch done — ${data.created} created${data.failed ? `, ${data.failed} failed` : ""}`);
      } else {
        toast.error("All creatives failed — check backend logs");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Batch failed");
    } finally {
      setRunning(false);
    }
  };

  const finish = () => {
    onDone?.();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md p-4 overflow-y-auto"
         data-testid="ad-batch-modal">
      <div className="min-h-full flex items-start justify-center py-8">
        <div className="w-full max-w-lg bg-[#0F0F0F] border border-white/10 rounded-xl overflow-hidden shadow-2xl">
          <div className="p-5 border-b border-white/10 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#FF5A1F] font-semibold mb-1">
                <Stack size={12} weight="fill" /> Batch generate
              </div>
              <div className="text-lg font-semibold text-white">
                Ad rotation for "{baseForm.product_focus || "…"}"
              </div>
              <div className="text-xs text-neutral-500 mt-1">
                Uses your current form settings as the base. Runs 3 in parallel.
              </div>
            </div>
            <button onClick={running ? undefined : onClose} className="text-neutral-500 hover:text-white disabled:opacity-30"
                    disabled={running} data-testid="ad-batch-close">
              <XIcon size={18} />
            </button>
          </div>

          {!result ? (
            <>
              <div className="p-5 space-y-5">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs uppercase tracking-wider text-neutral-500">How many creatives?</div>
                    <div className="text-2xl font-display font-semibold text-[#FF5A1F]"
                         data-testid="ad-batch-count-display">{count}</div>
                  </div>
                  <input
                    type="range"
                    min="2"
                    max="20"
                    value={count}
                    onChange={(e) => setCount(Number(e.target.value))}
                    disabled={running}
                    className="w-full accent-[#FF5A1F]"
                    data-testid="ad-batch-count-slider"
                  />
                  <div className="flex justify-between text-[10px] text-neutral-600 mt-1">
                    <span>2</span><span>10</span><span>20</span>
                  </div>
                </div>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={varyTone}
                    onChange={(e) => setVaryTone(e.target.checked)}
                    disabled={running}
                    className="accent-[#FF5A1F] mt-0.5"
                    data-testid="ad-batch-vary-tone"
                  />
                  <div>
                    <div className="text-sm text-white">Rotate through all 4 tones</div>
                    <div className="text-[11px] text-neutral-500 mt-0.5">
                      Cycle Money → Time → Peace of mind → Bold. Recommended for a diverse ad rotation.
                    </div>
                  </div>
                </label>
                <div className="rounded-lg border border-amber-500/25 bg-amber-500/5 p-3 text-xs text-amber-100">
                  <b>Heads-up:</b> {count} creatives = ~{Math.ceil(count / 3 * 35)}s wall-time and ~{count * 2}
                  <span> LLM calls charged against your Universal Key balance.</span>
                </div>
              </div>
              <div className="p-4 border-t border-white/10 bg-[#0A0A0A] flex items-center justify-between gap-3">
                <button
                  onClick={onClose}
                  disabled={running}
                  className="text-sm text-neutral-500 hover:text-white transition-colors disabled:opacity-40"
                >
                  Cancel
                </button>
                <button
                  onClick={start}
                  disabled={running}
                  data-testid="ad-batch-start-btn"
                  className="btn-primary rounded-full px-5 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-60"
                >
                  {running ? (
                    <>
                      <div className="w-3 h-3 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                      Generating {count}...
                    </>
                  ) : (
                    <>
                      <Stack size={13} weight="fill" /> Start batch
                    </>
                  )}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="p-5 space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <div className="card-surface p-3 text-center">
                    <div className="text-[10px] uppercase tracking-wider text-neutral-500">Requested</div>
                    <div className="text-2xl font-display font-semibold text-white mt-1">{result.batch_size}</div>
                  </div>
                  <div className="card-surface p-3 text-center">
                    <div className="text-[10px] uppercase tracking-wider text-neutral-500">Created</div>
                    <div className="text-2xl font-display font-semibold text-emerald-400 mt-1"
                         data-testid="ad-batch-result-created">
                      {result.created}
                    </div>
                  </div>
                  <div className="card-surface p-3 text-center">
                    <div className="text-[10px] uppercase tracking-wider text-neutral-500">Failed</div>
                    <div className={`text-2xl font-display font-semibold mt-1 ${result.failed ? "text-rose-300" : "text-neutral-600"}`}
                         data-testid="ad-batch-result-failed">
                      {result.failed}
                    </div>
                  </div>
                </div>
                <div className="space-y-1.5 max-h-64 overflow-y-auto">
                  {result.results.map((r, i) => (
                    <div
                      key={i}
                      className={`flex items-center gap-2 text-xs p-2 rounded border ${
                        r.status === "created"
                          ? "border-white/5 bg-white/[0.02] text-neutral-300"
                          : "border-rose-500/20 bg-rose-500/5 text-rose-200"
                      }`}
                    >
                      {r.status === "created"
                        ? <CheckCircle size={12} weight="fill" className="text-emerald-400 flex-shrink-0" />
                        : <WarningCircle size={12} weight="fill" className="text-rose-400 flex-shrink-0" />}
                      <span className="font-mono text-[10px] text-neutral-500 w-8">#{i + 1}</span>
                      <span className="uppercase tracking-widest text-[10px] text-neutral-500 w-24">
                        {r.creative?.tone || r.tone}
                      </span>
                      <span className="flex-1 truncate">
                        {r.status === "created"
                          ? r.creative?.copy?.headlines?.[0] || "(no headline)"
                          : r.error}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="p-4 border-t border-white/10 bg-[#0A0A0A] flex items-center justify-end">
                <button
                  onClick={finish}
                  data-testid="ad-batch-done-btn"
                  className="btn-primary rounded-full px-5 py-2 text-sm inline-flex items-center gap-2"
                >
                  See in library <CaretRight size={13} weight="bold" />
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminAdStudioPage() {
  const [form, setForm] = useState({
    product_focus: "",
    audience: "small_fleet",
    tone: "money",
    image_style: "dashboard_mockup",
    brand_notes: "",
    include_image: true,
  });
  const [generating, setGenerating] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [detailCreative, setDetailCreative] = useState(null);
  const [batchOpen, setBatchOpen] = useState(false);

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const { data } = await client.get("/admin/ad-studio/history");
      setHistory(data?.creatives || []);
    } catch {/* silent */}
    finally { setLoadingHistory(false); }
  };

  useEffect(() => { loadHistory(); }, []);

  const generate = async () => {
    if (!form.product_focus.trim()) {
      toast.error("Product focus is required");
      return;
    }
    setGenerating(true);
    try {
      const { data } = await client.post("/admin/ad-studio/generate", form);
      toast.success("Creative generated");
      setDetailCreative(data.creative);
      await loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally { setGenerating(false); }
  };

  const deleteCreative = async (cid) => {
    if (!window.confirm("Delete this creative? Image + copy will be removed from the library.")) return;
    try {
      await client.delete(`/admin/ad-studio/${cid}`);
      toast.success("Deleted");
      setDetailCreative(null);
      await loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="p-4 sm:p-8 max-w-[1400px]" data-testid="ad-studio-page">
      <div className="mb-6">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#FF5A1F] font-semibold mb-1">
          <Confetti size={12} weight="fill" /> Admin
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Ad Creative AI Studio</h1>
        <p className="text-sm text-neutral-400 mt-2 max-w-2xl">
          Generate on-brand Meta ad copy + hero image in one pass. GPT-5.2 writes 3 headlines, 3 primary texts,
          and 3 descriptions; Nano Banana generates a companion image. Everything is saved to your library.
        </p>
      </div>

      <div className="grid xl:grid-cols-[380px_1fr] gap-6">
        {/* Left column — Generator form */}
        <div className="card-surface p-5 space-y-5 h-fit" data-testid="ad-studio-form">
          <div>
            <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1.5">Product focus *</div>
            <input
              type="text"
              value={form.product_focus}
              onChange={(e) => setForm({ ...form, product_focus: e.target.value })}
              placeholder="e.g. AI Rate Negotiator, Weekly Payroll, Load Board"
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2 text-sm text-white"
              data-testid="ad-studio-product-input"
            />
          </div>

          <div>
            <div className="text-xs uppercase tracking-wider text-neutral-500 mb-2">Audience</div>
            <div className="space-y-2">
              {AUDIENCES.map((a) => pill(
                a.label,
                form.audience === a.id,
                () => setForm({ ...form, audience: a.id }),
                a.icon,
                a.subtitle,
                `ad-studio-audience-${a.id}`,
              ))}
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-wider text-neutral-500 mb-2">Tone</div>
            <div className="grid grid-cols-2 gap-2">
              {TONES.map((t) => pill(
                t.label,
                form.tone === t.id,
                () => setForm({ ...form, tone: t.id }),
                t.icon,
                t.subtitle,
                `ad-studio-tone-${t.id}`,
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-wider text-neutral-500">Image style</div>
              <label className="text-[11px] text-neutral-500 inline-flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.include_image}
                  onChange={(e) => setForm({ ...form, include_image: e.target.checked })}
                  className="accent-[#FF5A1F]"
                  data-testid="ad-studio-include-image"
                />
                Include image
              </label>
            </div>
            {form.include_image && (
              <div className="grid grid-cols-2 gap-2">
                {IMAGE_STYLES.map((s) => pill(
                  s.label,
                  form.image_style === s.id,
                  () => setForm({ ...form, image_style: s.id }),
                  s.icon,
                  s.subtitle,
                  `ad-studio-style-${s.id}`,
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1.5">Brand notes (optional)</div>
            <textarea
              rows={3}
              value={form.brand_notes}
              onChange={(e) => setForm({ ...form, brand_notes: e.target.value })}
              placeholder="Anything the copywriter should know — recent milestones, forbidden phrases, campaign goal..."
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2 text-sm text-white"
            />
          </div>

          <button
            onClick={generate}
            disabled={generating}
            data-testid="ad-studio-generate-btn"
            className="btn-primary rounded-full w-full py-3 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {generating ? (
              <>
                <div className="w-3 h-3 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                Generating — this takes 30-45 sec...
              </>
            ) : (
              <>
                <Sparkle size={13} weight="fill" /> Generate ad creative
              </>
            )}
          </button>

          <button
            onClick={() => setBatchOpen(true)}
            disabled={generating}
            data-testid="ad-studio-batch-btn"
            className="w-full rounded-full py-2.5 text-sm border border-white/15 hover:border-[#FF5A1F]/50 hover:text-white text-neutral-300 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-40"
          >
            <Stack size={13} /> Batch generate (2-20 at once)
          </button>
        </div>

        {/* Right column — History grid */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-sm text-white font-semibold">Recent creatives</div>
              <div className="text-xs text-neutral-500 mt-0.5">
                {history.length === 0 ? "None yet — generate your first above." : `${history.length} in your library`}
              </div>
            </div>
            <button
              onClick={loadHistory}
              disabled={loadingHistory}
              data-testid="ad-studio-refresh-btn"
              className="text-xs rounded-full px-3 py-1.5 border border-white/10 text-neutral-400 hover:text-white hover:border-white/25 transition-colors inline-flex items-center gap-1.5 disabled:opacity-60"
            >
              <ArrowClockwise size={11} /> Refresh
            </button>
          </div>
          {history.length === 0 ? (
            <div className="card-surface p-12 text-center">
              <Sparkle size={40} className="mx-auto text-[#FF5A1F] mb-3" weight="fill" />
              <div className="text-lg text-white font-semibold">No creatives yet</div>
              <div className="text-sm text-neutral-500 mt-1 max-w-md mx-auto">
                Fill in the form on the left and hit <span className="text-white">Generate ad creative</span>.
                Each run produces 9 copy variants + 1 hero image.
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {history.map((c) => (
                <CreativeCard
                  key={c.id}
                  creative={c}
                  onOpen={() => setDetailCreative(c)}
                  onDelete={() => deleteCreative(c.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <CreativeDetail
        creative={detailCreative}
        onClose={() => setDetailCreative(null)}
        onDelete={() => deleteCreative(detailCreative.id)}
      />

      <BatchModal
        open={batchOpen}
        baseForm={form}
        onClose={() => setBatchOpen(false)}
        onDone={loadHistory}
      />
    </div>
  );
}
